import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { IonContent, MenuController } from '@ionic/angular/standalone';
import { IconComponent } from '@shared/components/piscinapp-icon/icon.component';
import { Plan, Role } from '@shared/interfaces/user';
import { AuthenticationService } from '@shared/services/authentication/authentication.service';
import { ConfigService } from '@shared/services/config/config.service';
import { LocaleService } from '@shared/services/locale/locale.service';
import { NavControllerService } from '@shared/services/navigation/nav-controller.service';
import { NativeStorageService } from '@shared/services/storage/native-storage.service';
import { TranslateModule } from '@ngx-translate/core';
import { filter } from 'rxjs';

type MenuItem = {
  textKey: string;
  page: string;
  icon: string;
  isActive: boolean | undefined;
  isExpanded?: boolean;
  isHiddenOnBrowser?: boolean;
  isHiddenOutsideBrazil?: boolean;
  roles: Role[] | ('ADMIN' | 'FRANCHISEE' | 'EMPLOYEE' | 'LIMITED' | 'CLIENT')[];
  plans?: Plan[];
  children?: MenuItem[];
};

@Component({
  standalone: true,
  selector: 'app-navigation-menu',
  templateUrl: './navigation-menu.component.html',
  styleUrls: ['./navigation-menu.component.scss'],
  imports: [
    CommonModule,
    IconComponent,
    IonContent,
    TranslateModule,
  ],
})
export class NavigationMenuComponent implements OnInit {

  public menuItemsAvailable: MenuItem[] = [
    {
      textKey: 'NAVIGATION_MENU.ITEMS.START',
      page: 'start',
      icon: 'home.svg',
      isActive: undefined,
      isHiddenOnBrowser: true,
      roles: ['ADMIN', 'FRANCHISEE'],
    },
    {
      textKey: 'NAVIGATION_MENU.ITEMS.USERS',
      page: 'users-admin',
      icon: 'users-admin.svg',
      isActive: false,
      roles: ['ADMIN', 'FRANCHISEE'],
    },
    {
      textKey: 'NAVIGATION_MENU.ITEMS.PLACES',
      page: 'places-admin',
      icon: 'places-admin.svg',
      isActive: false,
      roles: ['ADMIN', 'FRANCHISEE'],
    },
    {
      textKey: 'NAVIGATION_MENU.ITEMS.COMMITMENTS',
      page: 'commitments-admin',
      icon: 'commitments-admin-thin.svg',
      isActive: false,
      roles: ['ADMIN', 'FRANCHISEE'],
      plans: [Plan.BUSINESS],
    },
    {
      textKey: 'NAVIGATION_MENU.ITEMS.PRODUCTS',
      page: 'products-admin',
      icon: 'products-admin.svg',
      isActive: false,
      roles: ['ADMIN'],
    },
    {
      textKey: 'NAVIGATION_MENU.ITEMS.EQUIPMENTS',
      page: 'equipments-admin',
      icon: 'equipments-admin.svg',
      isActive: false,
      roles: ['ADMIN', 'FRANCHISEE'],
      plans: [Plan.BUSINESS],
    },
    {
      textKey: 'NAVIGATION_MENU.ITEMS.STOCK_MOVEMENTS',
      page: 'stock-movements',
      icon: 'stock-movements.svg',
      isActive: false,
      roles: ['ADMIN', 'FRANCHISEE'],
    },
    {
      textKey: 'NAVIGATION_MENU.ITEMS.ALERTS',
      page: 'notifications',
      icon: 'notifications.svg',
      isActive: false,
      roles: ['ADMIN', 'FRANCHISEE'],
      plans: [Plan.BUSINESS],
    },
    {
      textKey: 'NAVIGATION_MENU.ITEMS.ANALYSES',
      page: 'analyzes-admin',
      icon: 'analyzes-admin.svg',
      isActive: false,
      roles: ['ADMIN', 'FRANCHISEE'],
      plans: [Plan.BUSINESS],
    },
    {
      textKey: 'NAVIGATION_MENU.ITEMS.ANALYSES',
      page: 'analyze-list',
      icon: 'analyzes-admin.svg',
      isActive: false,
      roles: ['CLIENT'],
    },
    {
      textKey: 'NAVIGATION_MENU.ITEMS.MONITORING',
      page: 'monitoring',
      icon: 'monitoring.svg',
      isActive: false,
      roles: ['ADMIN', 'FRANCHISEE'],
      plans: [Plan.BUSINESS],
    },
    {
      textKey: 'NAVIGATION_MENU.ITEMS.CLIENTS',
      page: 'clients',
      icon: 'person-outline.svg',
      isActive: false,
      roles: ['ADMIN', 'FRANCHISEE'],
    },
    {
      textKey: 'NAVIGATION_MENU.ITEMS.REPORTS',
      page: 'reports',
      icon: 'reports.svg',
      isActive: false,
      roles: ['ADMIN', 'FRANCHISEE'],
    },
    {
      textKey: 'NAVIGATION_MENU.ITEMS.LEADS',
      page: 'leads',
      icon: 'leads.svg',
      isActive: false,
      isHiddenOutsideBrazil: true,
      roles: ['ADMIN', 'FRANCHISEE'],
    },
    {
      textKey: 'NAVIGATION_MENU.ITEMS.QR_CODE',
      page: 'qr-code',
      icon: 'qr-code.svg',
      isActive: false,
      isHiddenOnBrowser: true,
      roles: ['ADMIN', 'FRANCHISEE'],

    },
    {
      textKey: 'NAVIGATION_MENU.ITEMS.ADMINISTRATION',
      page: 'support-tickets',
      icon: 'settings.svg',
      isActive: false,
      isExpanded: false,
      roles: ['ADMIN'],
      children: [
        {
          textKey: 'NAVIGATION_MENU.ITEMS.SUPPORT',
          page: 'support-tickets',
          icon: 'support-chat.svg',
          isActive: false,
          roles: ['ADMIN'],
        },
        {
          textKey: 'NAVIGATION_MENU.ITEMS.PRODUCT_SUGGESTION_RULES',
          page: 'product-suggestion-rules-admin',
          icon: 'test-tube.svg',
          isActive: false,
          roles: ['ADMIN'],
        },
        {
          textKey: 'NAVIGATION_MENU.ITEMS.PROTOCOLS',
          page: 'protocols-admin',
          icon: 'test-tube.svg',
          isActive: false,
          roles: ['ADMIN'],
        },
        {
          textKey: 'NAVIGATION_MENU.ITEMS.SPECIAL_TREATMENTS',
          page: 'special-treatment-details-admin',
          icon: 'test-tube.svg',
          isActive: false,
          roles: ['ADMIN'],
        },
      ],
    },
    /*     {
          textKey: 'NAVIGATION_MENU.ITEMS.SUPPORT',
          page: 'support-chat',
          icon: 'support-chat.svg',
          isActive: false
        }, */
  ];

  public menuItems: MenuItem[] = [];

  @Input() public role: Role;

  @Input() public plan: Plan;

  private isMobileApp = this.configService.isMobileApp;

  private expandedMenuPages = new Set<string>();

  constructor(
    private navCtrl: NavControllerService,
    private nativeStorage: NativeStorageService,
    private menuCtrl: MenuController,
    private configService: ConfigService,
    private authService: AuthenticationService,
    private router: Router,
    private localeService: LocaleService,
  ) { }

  ngOnInit() {
    if (this.authService.user) {
      this.updateMenuItemsBasedOnUser(this.authService.user);
    }
    this.authService.subscribeUserUpdate(user => {
      this.updateMenuItemsBasedOnUser(user);
    });
    this.localeService.subscribeIsUserInBrazilChange(() => {
      if (this.authService.user) {
        this.updateMenuItemsBasedOnUser(this.authService.user);
      }
    });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.syncMenuStateWithPath(true));
  }

  private updateMenuItemsBasedOnUser(user: any) {
    if (!user) {
      this.menuItems = [];
      return;
    }

    this.role = user.roles[0];
    this.plan = user?.plan;
    this.menuItems = this.buildMenuItems(this.menuItemsAvailable);
    this.syncMenuStateWithPath(true);

    if (this.menuItems.length === 0) {
      this.configService.isMenuShown = false;
    }
  }

  private buildMenuItems(items: MenuItem[]): MenuItem[] {
    return items
      .map((item) => this.buildMenuItem(item))
      .filter((item): item is MenuItem => Boolean(item));
  }

  private buildMenuItem(item: MenuItem): MenuItem | null {
    if (!this.isMobileApp && item.isHiddenOnBrowser) {
      return null;
    }

    if (item.isHiddenOutsideBrazil && !this.localeService.isUserInBrazil) {
      return null;
    }

    if (!this.hasUserRole(item.roles as Role[]) || !this.hasPlanAccess(item.plans)) {
      return null;
    }

    const children = item.children?.length
      ? this.buildMenuItems(item.children)
      : undefined;

    return {
      ...item,
      page: this.resolveMenuItemPage(item),
      isActive: false,
      isExpanded: false,
      children,
    };
  }

  private resolveMenuItemPage(menuItem: MenuItem) {
    if (menuItem.page === 'commitments-admin' && this.role !== 'ADMIN') {
      return 'commitments-schedule';
    }
    return menuItem.page;
  }

  handleMenuItemClick(event: MouseEvent, menuItem: MenuItem) {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();

    if (this.isPageActive(menuItem.page)) {
      return;
    }

    this.menuCtrl.close();
    if (menuItem.page === 'start') {
      return this.goToStart();
    }

    this.navCtrl.setRoot(menuItem.page, {}, { replaceUrl: this.isMobileApp });
  }

  handleExpandIconClick(event: MouseEvent, menuItem: MenuItem) {
    event.preventDefault();
    event.stopPropagation();

    if (this.expandedMenuPages.has(menuItem.page)) {
      this.expandedMenuPages.delete(menuItem.page);
    } else {
      this.expandedMenuPages.add(menuItem.page);
    }

    this.syncMenuStateWithPath();
  }

  goToStart() {
    this.nativeStorage.getItem('startPage')
      .then(startPage => this.navCtrl.setRoot(startPage));
  }

  hasChildren(menuItem: MenuItem) {
    return Boolean(menuItem.children?.length);
  }

  private syncMenuStateWithPath(shouldCollapseInactiveExpandedMenus = false) {
    const pathname = this.getPathname();
    this.menuItems.forEach((item) => this.applyMenuState(item, pathname, shouldCollapseInactiveExpandedMenus));
  }

  private applyMenuState(menuItem: MenuItem, pathname: string, shouldCollapseInactiveExpandedMenus = false): boolean {
    let hasActiveChild = false;

    (menuItem.children || []).forEach((child) => {
      const isChildActive = this.applyMenuState(child, pathname, shouldCollapseInactiveExpandedMenus);
      hasActiveChild = hasActiveChild || isChildActive;
    });

    const isCurrentPage = this.isPageActive(menuItem.page, pathname);

    menuItem.isActive = isCurrentPage || hasActiveChild;

    if (shouldCollapseInactiveExpandedMenus && this.hasChildren(menuItem) && !menuItem.isActive) {
      this.expandedMenuPages.delete(menuItem.page);
    }

    menuItem.isExpanded = this.hasChildren(menuItem)
      ? this.expandedMenuPages.has(menuItem.page) || menuItem.isActive
      : false;

    return Boolean(menuItem.isActive);
  }

  private isPageActive(page: string, pathname = this.getPathname()) {
    const normalizedPage = page.replace(/^\/+|\/+$/g, '');
    const normalizedPathname = pathname.replace(/^\/+|\/+$/g, '');

    return normalizedPathname === normalizedPage || normalizedPathname.startsWith(`${normalizedPage}/`);
  }

  getPathname() {
    return window.location.pathname;
  }

  hasUserRole(roles: Role[]) {
    if (!roles) {
      return true;
    };
    return roles.includes(this.role);
  }

  hasPlanAccess(plans: Plan[]) {
    if (this.role === 'ADMIN') {
      return true;
    }
    if (!plans || plans.length === 0) {
      return true;
    }
    return plans.includes(this.plan);
  }

}
